import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { replacePiiDeleted } from '../../../assets/utils/replacePiiDeleted';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import { setRedactedDescriptionPositions } from '../../../slices/reportDataSlice';
import { RootState } from '../../../utils/store';

const RedactTextBox = () => {
  const intl = useIntl();

  const dispatch = useDispatch();
  const [redactedPositions, setRedactedPositions] = useState<
  [number, number][]
  >([]);
  const [highlightedText, setHighlightedText] = useState<string>('');
  const { createToastNotification } = useToastNotificationContext();

  const { description } = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const redactedDescriptionPositions = useSelector(
    (state: RootState) => state.reportData.redactedDescriptionPositions,
  );

  useEffect(() => {
    if (redactedDescriptionPositions)
      setRedactedPositions(redactedDescriptionPositions);
  }, [redactedDescriptionPositions, description]);

  const applyHighlight = useCallback(() => {
    const newRedactedPositions = [...redactedPositions];
    let redactedText = '';
    let lastIndex = 0;

    if (newRedactedPositions) {
      // Sort ranges based on start index
      newRedactedPositions.sort((a, b) => a[0] - b[0]);

      newRedactedPositions.forEach((range) => {
        const [start, end] = range; // Subtract 11 from each value

        // Append non-highlighted text
        redactedText += description.substring(lastIndex, start);

        // Append highlighted text
        redactedText += `<span style="background-color: yellow">${description.substring(
          start,
          end,
        )}</span>`;

        lastIndex = end;
      });

      // Append any remaining non-highlighted text
      redactedText += description.substring(lastIndex);
    }

    const replacedText = replacePiiDeleted(redactedText);

    return replacedText;
  }, [redactedPositions, description]);

  useEffect(() => {
    setHighlightedText(applyHighlight());
  }, [redactedPositions, applyHighlight]);

  const handleRedact = () => {
    const selection = window.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0);
      const startContainer = range.startContainer;
      const endContainer = range.endContainer;

      const piiDeletedRanges: [number, number][] = [];
      const piiDeletedRegex = /\*PII Deleted\*/g;
      let match;

      while ((match = piiDeletedRegex.exec(description)) !== null) {
        piiDeletedRanges.push([match.index, match.index + match[0].length]);
      }

      // Get the full text content
      const fullText = description;

      // Calculate the start and end positions
      let startPosition = range.startOffset;
      let endPosition = range.endOffset;

      // Count characters before the start position
      if (startContainer && startContainer.nodeType === Node.TEXT_NODE) {
        const startText = (startContainer as Text).textContent || '';
        startPosition += fullText.indexOf(startText);
      }

      // Count characters before the end position
      if (endContainer && endContainer.nodeType === Node.TEXT_NODE) {
        const endText = (endContainer as Text).textContent || '';
        endPosition += fullText.indexOf(endText);
      }
      // Check if the selected range overlaps with any existing highlighted positions
      const overlappingPosition = redactedDescriptionPositions?.find(
        ([start, end]) =>
          (start <= startPosition && end >= startPosition) ||
          (start <= endPosition && end >= endPosition),
      );

      const checkForDeletedPositions = piiDeletedRanges.find(
        ([start, end]) =>
          (start <= startPosition && end >= startPosition) ||
          (start <= endPosition && end >= endPosition),
      );

      if (overlappingPosition) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage:
              'This text has already been selected to be redacted.',
          }),
          'error',
        );
      } else if (checkForDeletedPositions) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'You cannot delete PII Deleted text.',
          }),
          'error',
        );
      } else if (startPosition <= 1 && endPosition <= 1) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage:
              'Redaction Not Complete: Ensure you select the text you want to redact within the report.',
          }),
          'error',
        );
        startPosition = 0;
        endPosition = 0;
      } else {
        // Update redacted positions
        dispatch(
          setRedactedDescriptionPositions([
            ...redactedPositions,
            [startPosition, endPosition],
          ]),
        );
      }
    }
  };

  const handleUnredact = () => {
    // Clear all existing highlights
    const highlightedSpans = document.querySelectorAll('.highlight');
    highlightedSpans.forEach((span) => {
      const parent = span.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(span.textContent!), span);
      }
    });
    dispatch(setRedactedDescriptionPositions([]));
  };

  return (
    <Box>
      <Box
        contentEditable={false}
        sx={{
          border: '1px solid black',
          padding: '12px',
          borderRadius: '6px',
          minHeight: '100px',
          cursor: 'text',
          overflowY: 'auto', // Enable vertical scroll if content exceeds box height
          maxHeight: '200px', // Set a maximum height for the box to trigger scrolling
        }}
        dangerouslySetInnerHTML={{ __html: highlightedText }}
      />
      {/* Buttons Box */}
      <Box sx={{ mt: '12px' }}>
        <Button
          onClick={handleRedact}
          sx={{
            textTransform: 'none',
            color: 'rgba(93, 93, 93, 1)',
            fontWeight: 600,
            fontSize: '14px',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          Redact
        </Button>
        <Button
          onClick={handleUnredact}
          sx={{
            textTransform: 'none',
            color: 'rgba(93, 93, 93, 1)',
            fontWeight: 600,
            fontSize: '14px',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          Remove all redacted
        </Button>
      </Box>
      <style>
        {`
        .highlight {
          background-color: black;
          color: black; // Adjust text color for visibility if needed
        }
      `}
      </style>
    </Box>
  );
};

export default RedactTextBox;
